import type { ISO8601DateOnly } from '../cross-app-shared/types/common.ts';

export const HAZ_WASTE_CONTAINER_LIFECYCLE_STATUSES = [
  'staged',
  'active',
  'awaiting-shipment',
  'shipped',
  'retired',
] as const;

export type HazWasteContainerLifecycleStatus = (typeof HAZ_WASTE_CONTAINER_LIFECYCLE_STATUSES)[number];

export type HazWasteContainerEventType =
  | 'created'
  | 'activated'
  | 'accumulation-start-date-set'
  | 'moved-to-caa'
  | 'awaiting-shipment'
  | 'shipped'
  | 'retired'
  | 'removed';

export const hazWasteContainerEventLabels: Record<HazWasteContainerEventType, string> = {
  created: 'Container added',
  activated: 'Accumulation started',
  'accumulation-start-date-set': 'Accumulation start date recorded',
  'moved-to-caa': 'Moved to central accumulation area',
  'awaiting-shipment': 'Assigned to an outbound shipment',
  shipped: 'Moved out with shipment',
  retired: 'Container retired',
  removed: 'Container removed from area',
};

const allowedTransitions: Record<HazWasteContainerLifecycleStatus, readonly HazWasteContainerLifecycleStatus[]> = {
  staged: ['active', 'retired'],
  active: ['awaiting-shipment', 'retired'],
  'awaiting-shipment': ['active', 'shipped', 'retired'],
  shipped: ['retired'],
  retired: [],
};

export function canTransitionHazWasteContainer(
  from: HazWasteContainerLifecycleStatus,
  to: HazWasteContainerLifecycleStatus,
): boolean {
  return allowedTransitions[from].includes(to);
}

/** Whether a container is still physically held at the facility: not archived, shipped, or retired. */
export function hazWasteContainerInService(container: {
  hazWasteLifecycleStatus: HazWasteContainerLifecycleStatus;
  archivedAt: string | null;
}): boolean {
  return (
    container.archivedAt == null &&
    container.hazWasteLifecycleStatus !== 'shipped' &&
    container.hazWasteLifecycleStatus !== 'retired'
  );
}

/** Whether a container's waste stream can no longer be changed from its accumulation area. Once a container leaves
 * staging or starts an accumulation clock it holds that stream's waste, so it must be retired or replaced instead. */
export function hazWasteContainerStreamAssignmentLocked(container: {
  hazWasteLifecycleStatus: HazWasteContainerLifecycleStatus;
  accumulationStartDate: ISO8601DateOnly | null;
  wasteStreamAccumulationStartDate: ISO8601DateOnly | null;
}): boolean {
  return (
    container.hazWasteLifecycleStatus !== 'staged' ||
    container.accumulationStartDate != null ||
    container.wasteStreamAccumulationStartDate != null
  );
}

export interface HazWasteContainerShipmentCandidate {
  facilityStorageVesselID: number;
  hazWasteLifecycleStatus: HazWasteContainerLifecycleStatus;
  hazWasteStreamID: number | null;
}

/**
 * Why the selected containers can't go out on a shipment (standard or e-Manifest) carrying `selectedStreamIDs`.
 * `containers` are the active rows found for `requestedIDs` at the facility. Empty when every container can ship.
 */
export function hazWasteContainersShipmentErrors(
  requestedIDs: readonly number[],
  containers: readonly HazWasteContainerShipmentCandidate[],
  selectedStreamIDs: ReadonlySet<number>,
): string[] {
  const errors: string[] = [];
  if (containers.length !== requestedIDs.length) {
    errors.push('One or more selected containers are unavailable.');
  }
  for (const container of containers) {
    if (container.hazWasteStreamID == null || !selectedStreamIDs.has(container.hazWasteStreamID)) {
      errors.push(
        `Container ${container.facilityStorageVesselID} does not contain a waste stream selected for this shipment.`,
      );
    }
    if (!canTransitionHazWasteContainer(container.hazWasteLifecycleStatus, 'awaiting-shipment')) {
      errors.push(
        `Container ${container.facilityStorageVesselID} cannot be shipped because it is ${container.hazWasteLifecycleStatus}.`,
      );
    }
  }
  return errors;
}

/** Why `moveDate` can't start a satellite container's central accumulation clock, or null when it can. The CAA clock
 * can't start before the container began accumulating in its satellite area, or in the future. `today` is the
 * facility's local date. */
export function hazWasteCaaMoveDateError(
  moveDate: ISO8601DateOnly,
  satelliteAccumulationStartDate: ISO8601DateOnly | null,
  today: ISO8601DateOnly,
): string | null {
  if (moveDate > today) return 'The accumulation start date cannot be in the future.';
  if (satelliteAccumulationStartDate && moveDate < satelliteAccumulationStartDate)
    return 'The CAA start date cannot be before the satellite accumulation start date.';
  return null;
}
